import { useId } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';

export type IconWeight =
    | 'thin'
    | 'light'
    | 'regular'
    | 'bold'
    | 'fill'
    | 'duotone';

export interface RichSelectOption {
    value: string;
    title: string;
    description: string;
    icon?: Icon;
    iconWeight?: IconWeight;
}

interface RichSelectProps {
    label?: string;
    value: string | null;
    options: RichSelectOption[];
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    triggerClassName?: string;
    name?: string;
    disabled?: boolean;
    required?: boolean;
    'aria-label'?: string;
}

export function RichSelect({
    label,
    value,
    options,
    onChange,
    placeholder,
    className,
    triggerClassName,
    name,
    disabled,
    required,
    'aria-label': ariaLabel,
}: RichSelectProps) {
    const triggerId = useId();
    const selected =
        value !== null ? options.find((o) => o.value === value) : null;

    return (
        <div className={cn('flex w-full flex-col gap-3', className)}>
            {label && (
                <Label
                    htmlFor={triggerId}
                    required={required}
                    className={cn(
                        'text-sm-medium text-white-100',
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                >
                    {label}
                </Label>
            )}
            <SelectPrimitive.Root
                value={value ?? undefined}
                onValueChange={onChange}
                name={name}
                disabled={disabled}
                required={required}
            >
                <SelectPrimitive.Trigger
                    id={triggerId}
                    className={cn(
                        'box-border flex w-full min-w-0 flex-row items-center justify-between gap-4 rounded-lg border border-grey-500 bg-transparent px-4 py-3 text-left text-sm-medium text-white-100 outline-none transition-colors hover:border-grey-400 focus:border-grey-400 focus:ring-1 focus:ring-grey-500 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-grey-300',
                        triggerClassName,
                    )}
                    aria-label={ariaLabel ?? label ?? 'Select option'}
                    aria-required={required ? true : undefined}
                >
                    <SelectPrimitive.Value
                        className="flex min-w-0 flex-1 flex-row items-center gap-4 overflow-hidden text-left text-sm-medium"
                        placeholder={
                            <span className="flex min-w-0 flex-1 flex-row items-center gap-4">
                                <span className="min-w-0 flex-1 text-sm-medium text-grey-300">
                                    {placeholder ?? 'Select…'}
                                </span>
                            </span>
                        }
                    >
                        {selected ? (
                            <span className="flex min-w-0 flex-1 flex-row items-center gap-4">
                                {selected.icon ? (
                                    <selected.icon
                                        className="size-5 shrink-0 text-grey-200"
                                        weight={
                                            selected.iconWeight ?? 'regular'
                                        }
                                        aria-hidden
                                    />
                                ) : null}
                                <span className="flex min-w-0 flex-1 flex-col justify-center gap-1 text-left">
                                    <span className="text-sm-medium text-white-100">
                                        {selected.title}
                                    </span>
                                    <span className="text-xs-medium text-grey-300">
                                        {selected.description}
                                    </span>
                                </span>
                            </span>
                        ) : null}
                    </SelectPrimitive.Value>
                    <SelectPrimitive.Icon asChild>
                        <CaretDownIcon
                            className="size-4 shrink-0 text-grey-200"
                            aria-hidden
                        />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>
                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                        position="popper"
                        sideOffset={4}
                        align="start"
                        className="z-50 w-[var(--radix-select-trigger-width)] min-w-0 overflow-hidden rounded-lg border border-grey-500 bg-grey-700 p-2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
                    >
                        <SelectPrimitive.Viewport>
                            {options.map((option) => {
                                const IconComponent = option.icon;
                                return (
                                    <SelectPrimitive.Item
                                        key={option.value}
                                        value={option.value}
                                        textValue={option.title}
                                        className="flex w-full min-w-0 cursor-pointer flex-row items-center justify-between gap-4 rounded-lg px-4 py-3 text-sm-medium text-white outline-none focus:bg-grey-650 data-[highlighted]:bg-grey-650"
                                    >
                                        {IconComponent && (
                                            <IconComponent
                                                className="size-5 shrink-0 text-grey-200"
                                                weight={
                                                    option.iconWeight ??
                                                    'regular'
                                                }
                                                aria-hidden
                                            />
                                        )}
                                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
                                            <SelectPrimitive.ItemText className="text-sm-medium !text-white">
                                                {option.title}
                                            </SelectPrimitive.ItemText>
                                            <span className="text-xs-medium text-grey-300">
                                                {option.description}
                                            </span>
                                        </div>
                                        <SelectPrimitive.ItemIndicator>
                                            <CheckIcon
                                                className="size-4 shrink-0 text-white"
                                                aria-hidden
                                            />
                                        </SelectPrimitive.ItemIndicator>
                                    </SelectPrimitive.Item>
                                );
                            })}
                        </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
        </div>
    );
}
